import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Video, BookOpen, Users, ArrowLeft, Play, Clock, DollarSign, MessageSquare, Heart } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { dateLocale } from "@/lib/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ============================================================
// CARE — Liste sessions vidéo
// ============================================================
export function SolunaryCare() {
  const [sessions, setSessions] = useState(null);
  const t = useT("saas");
  const tc = useT("common");
  const { lang } = useLanguage();
  useEffect(() => {
    axios.get(`${API}/p3/care/sessions`).then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, []);

  return (
    <div className="min-h-screen bg-sol-midnight text-white">
      <div className="max-w-5xl mx-auto px-6 py-20">
        <Link to="/" className="text-sm text-white/40 hover:text-sol-gold inline-flex items-center gap-2 mb-8">
          <ArrowLeft className="w-4 h-4" /> {tc("actions.back")}
        </Link>
        <div className="mb-12 flex items-start gap-4">
          <Video className="w-12 h-12 text-sol-gold mt-2" />
          <div>
            <h1 className="font-display font-semibold text-5xl mb-2 bg-gradient-to-r from-sol-gold via-rose-200 to-sol-sage bg-clip-text text-transparent">Solunary Care</h1>
            <p className="text-white/60">{t("p3.care.subtitle")}</p>
          </div>
        </div>

        {!sessions ? <Loader2 className="w-6 h-6 animate-spin text-sol-gold" /> :
         sessions.length === 0 ? (
          <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-12 text-center">
            <p className="text-white/40 mb-4">{t("p3.care.empty")}</p>
            <p className="text-xs text-white/30">{t("p3.care.emptyHint")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {sessions.map((s) => (
              <div key={s.session_id} className="rounded-2xl bg-white/[0.02] border border-white/10 p-6" data-testid={`care-${s.session_id}`}>
                <div className="text-xs text-sol-gold mb-2">{s.status}</div>
                <div className="text-sm text-white/70 mb-1">{s.practitioner_email}</div>
                <div className="text-xs text-white/50 mb-3">{new Date(s.scheduled_at).toLocaleString(dateLocale(lang))} · {s.duration_min} min</div>
                <a href={s.room_url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-2 text-sm text-sol-gold hover:text-sol-gold/80">
                  <Play className="w-3.5 h-3.5" /> {t("p3.care.joinSession")}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// LEARN — Catalogue de cours
// ============================================================
export function SolunaryLearn() {
  const [courses, setCourses] = useState(null);
  const t = useT("saas");
  const tc = useT("common");
  useEffect(() => {
    axios.get(`${API}/p3/learn/courses`).then((r) => setCourses(r.data)).catch(() => setCourses([]));
  }, []);

  return (
    <div className="min-h-screen bg-sol-midnight text-white">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <Link to="/" className="text-sm text-white/40 hover:text-sol-gold inline-flex items-center gap-2 mb-8">
          <ArrowLeft className="w-4 h-4" /> {tc("actions.back")}
        </Link>
        <div className="mb-12 flex items-start gap-4">
          <BookOpen className="w-12 h-12 text-sol-gold mt-2" />
          <div>
            <h1 className="font-display font-semibold text-5xl mb-2 bg-gradient-to-r from-sol-gold via-rose-200 to-sol-sage bg-clip-text text-transparent">Solunary Learn</h1>
            <p className="text-white/60">{t("p3.learn.subtitle")}</p>
          </div>
        </div>

        {!courses ? <Loader2 className="w-6 h-6 animate-spin text-sol-gold" /> :
         courses.length === 0 ? (
          <p className="text-white/40 text-center py-12">{t("p3.learn.empty")}</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {courses.map((c) => (
              <Link key={c.course_id} to={`/learn/${c.course_id}`} className="group rounded-2xl bg-white/[0.02] border border-white/10 p-6 hover:border-sol-gold/30 transition" data-testid={`course-${c.course_id}`}>
                <h3 className="font-display text-xl mb-2 group-hover:text-sol-gold transition">{c.title}</h3>
                <p className="text-sm text-white/60 mb-4 line-clamp-2">{c.description}</p>
                <div className="flex items-center gap-4 text-xs text-white/40">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {c.duration_min} min</span>
                  <span className="inline-flex items-center gap-1"><DollarSign className="w-3 h-3" /> {c.price} {c.currency}</span>
                  <span>{(c.modules || []).length} modules</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function SolunaryLearnCourse() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const t = useT("saas");
  useEffect(() => {
    axios.get(`${API}/p3/learn/courses`).then((r) => {
      setCourse((r.data || []).find((c) => c.course_id === courseId) || null);
    });
  }, [courseId]);

  if (!course) return <div className="min-h-screen bg-sol-midnight flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sol-gold" /></div>;

  return (
    <div className="min-h-screen bg-sol-midnight text-white">
      <div className="max-w-4xl mx-auto px-6 py-20">
        <Link to="/learn" className="text-sm text-white/40 hover:text-sol-gold inline-flex items-center gap-2 mb-8">
          <ArrowLeft className="w-4 h-4" /> {t("p3.learn.allCourses")}
        </Link>
        <h1 className="font-display font-semibold text-5xl mb-2">{course.title}</h1>
        <p className="text-white/60 mb-8">{course.description}</p>
        <div className="rounded-2xl bg-white/[0.02] border border-sol-gold/30 p-8 mb-8">
          <div className="flex items-center justify-between">
            <div className="text-3xl font-display text-sol-gold">{course.price} {course.currency}</div>
            <button className="px-6 py-3 rounded-full bg-sol-gold text-black hover:bg-amber-200 font-medium" data-testid="enroll-btn">
              {t("p3.learn.enroll")}
            </button>
          </div>
        </div>
        <h2 className="font-display text-2xl mb-4">{t("p3.learn.courseContent")}</h2>
        <div className="space-y-2">
          {(course.modules || []).map((m, i) => (
            <div key={i} className="rounded-xl bg-white/[0.02] border border-white/10 p-4 flex items-center gap-4">
              <div className="w-8 h-8 rounded-full bg-sol-gold/10 text-sol-gold flex items-center justify-center text-sm">{i + 1}</div>
              <div className="flex-1">
                <div className="font-medium">{m.title}</div>
                <div className="text-xs text-white/40">{m.duration_min} min</div>
              </div>
              <Play className="w-4 h-4 text-white/30" />
            </div>
          ))}
          {(course.modules || []).length === 0 && <p className="text-white/40 text-sm">{t("p3.learn.noModules")}</p>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CIRCLE — Feed communauté
// ============================================================
export function SolunaryCircle() {
  const [posts, setPosts] = useState(null);
  const [composer, setComposer] = useState({ title: "", body: "", author_email: "" });
  const t = useT("saas");
  const tc = useT("common");
  const { lang } = useLanguage();

  const load = () => axios.get(`${API}/p3/circle/posts`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!composer.title || !composer.body || !composer.author_email) return;
    await axios.post(`${API}/p3/circle/posts`, composer);
    setComposer({ title: "", body: "", author_email: "" });
    load();
  };

  return (
    <div className="min-h-screen bg-sol-midnight text-white">
      <div className="max-w-3xl mx-auto px-6 py-20">
        <Link to="/" className="text-sm text-white/40 hover:text-sol-gold inline-flex items-center gap-2 mb-8">
          <ArrowLeft className="w-4 h-4" /> {tc("actions.back")}
        </Link>
        <div className="mb-10 flex items-start gap-4">
          <Users className="w-12 h-12 text-sol-gold mt-2" />
          <div>
            <h1 className="font-display font-semibold text-5xl mb-2 bg-gradient-to-r from-sol-gold via-rose-200 to-sol-sage bg-clip-text text-transparent">Solunary Circle</h1>
            <p className="text-white/60">{t("p3.circle.subtitle")}</p>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl bg-white/[0.02] border border-white/10 p-6 mb-8" data-testid="circle-composer">
          <input type="email" placeholder={t("p3.circle.emailPlaceholder")} required value={composer.author_email}
            onChange={(e) => setComposer({ ...composer, author_email: e.target.value })}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm mb-2" />
          <input placeholder={t("p3.circle.titlePlaceholder")} required value={composer.title}
            onChange={(e) => setComposer({ ...composer, title: e.target.value })}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm mb-2" data-testid="post-title" />
          <textarea placeholder={t("p3.circle.bodyPlaceholder")} rows={3} required value={composer.body}
            onChange={(e) => setComposer({ ...composer, body: e.target.value })}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="post-body" />
          <button type="submit" className="mt-3 px-4 py-2 rounded-full bg-sol-gold text-black hover:bg-amber-200 text-sm" data-testid="post-submit">
            {t("p3.circle.submit")}
          </button>
        </form>

        {!posts ? <Loader2 className="w-6 h-6 animate-spin text-sol-gold" /> :
         posts.length === 0 ? <p className="text-white/40 text-center py-12">{t("p3.circle.empty")}</p> : (
          <div className="space-y-4">
            {posts.map((p) => (
              <article key={p.post_id} className="rounded-2xl bg-white/[0.02] border border-white/10 p-6" data-testid={`circle-post-${p.post_id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sol-gold to-rose-400 flex items-center justify-center text-xs font-semibold text-black">
                    {(p.author_email || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{p.author_email}</div>
                    <div className="text-xs text-white/40">{new Date(p.created_at).toLocaleDateString(dateLocale(lang))}</div>
                  </div>
                </div>
                <h3 className="font-display text-2xl mb-2">{p.title}</h3>
                <p className="text-white/70 text-sm whitespace-pre-wrap mb-4">{p.body}</p>
                <div className="flex items-center gap-4 text-xs text-white/40">
                  <button className="inline-flex items-center gap-1 hover:text-rose-300"><Heart className="w-3.5 h-3.5" /> {p.likes || 0}</button>
                  <button className="inline-flex items-center gap-1 hover:text-sol-gold"><MessageSquare className="w-3.5 h-3.5" /> {p.replies_count || 0}</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
